import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { FileText, FileCheck, FileSignature, Shield, ChevronRight, ChevronLeft, ChevronDown, Folder, X } from 'lucide-react';
import { databaseAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const PreviewContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const PreviewModal = styled.div`
  background: white;
  border-radius: 8px;
  max-width: calc(100vw - 100px);
  max-height: calc(100vh - 40px);
  min-height: calc(100vh - 100px);
  width: 100%;
  min-width: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const PreviewHeader = styled.div`
  padding: 1rem;
  padding-left: ${props => props.hasSidebar ? '2rem' : '1rem'};
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  transition: padding-left 0.3s ease;
`;

const PreviewTitle = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
  cursor: help;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;


const ActionButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
    border-color: #9ca3af;
  }
`;

const PreviewContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 1rem;
  background: white;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #6b7280;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #e74c3c;
  text-align: center;
  padding: 1rem;
`;

const PDFPreview = styled.iframe`
  width: 100%;
  height: 600px;
  border: none;
  border-radius: 4px;
`;

const JSONPreview = styled.pre`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 1rem;
  overflow: auto;
  max-height: 600px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const TextPreview = styled.pre`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 1rem;
  overflow: auto;
  max-height: 600px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const TableContainer = styled.div`
  overflow: auto;
  max-height: 600px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
`;

const CSVTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  
  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
  }
  
  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #495057;
    position: sticky;
    top: 0;
  }
  
  tr:hover {
    background: #f8f9fa;
  }
`;

const ExcelPreview = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 1rem;
  text-align: center;
  color: #6b7280;
`;

const ImagePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 1rem;
`;

const UnsupportedPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #6b7280;
  text-align: center;
  padding: 2rem;
`;

const FileIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const Sidebar = styled.div`
  width: ${props => props.isCollapsed ? '0' : '300px'};
  background: #f8f9fa;
  border-right: ${props => props.isCollapsed ? 'none' : '1px solid #e9ecef'};
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  transition: width 0.3s ease;
`;

const SidebarToggle = styled.button`
  position: absolute;
  top: calc(1rem + 2px);
  left: ${props => props.isCollapsed ? '-6px' : '0px'};
  width: 24px;
  height: 34px;
  background: #ea580c;
  color: white;
  border: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: left 0.3s ease;
  
  &:hover {
    background: #dc2626;
  }
`;

const SidebarHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DocumentGroup = styled.div`
  border-bottom: 1px solid #e9ecef;
`;

const GroupHeader = styled.div`
  padding: 0.75rem 1rem;
  background: ${props => props.isExpanded ? '#f1f5f9' : '#fff'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  color: #495057;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.isExpanded ? '#f1f5f9' : '#f8f9fa'};
  }
`;

const GroupIcon = styled.span`
  font-size: 1.2rem;
  margin-right: 0.5rem;
`;

const ExpandIcon = styled.div`
  transition: transform 0.2s;
  transform: ${props => props.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
`;

const DocumentList = styled.div`
  max-height: ${props => props.isExpanded ? 'none' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const DocumentItem = styled.div`
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.isActive ? '#fef3e7' : 'transparent'};
  color: ${props => props.isActive ? '#ea580c' : '#495057'};
  border-left: ${props => props.isActive ? '3px solid #ea580c' : '3px solid transparent'};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.isActive ? '#fef3e7' : '#f8f9fa'};
  }
`;

const DocumentIcon = styled.span`
  font-size: 1rem;
  opacity: 0.7;
`;

const DocumentName = styled.span`
  font-size: 0.875rem;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const DocumentDate = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.125rem;
  text-align: left;
`;

const DocumentButtons = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const DocumentButton = styled.button`
  padding: 0;
  font-size: 0.75rem;
  background: transparent;
  color: #3b82f6;
  border: none;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: #2563eb;
  }
`;

const DocumentInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const FileTypeCircle = styled.div`
  width: 27px;
  height: 27px;
  border-radius: 50%;
  background-color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
`;

// Mock data generators
const generateMockPDFContent = () => {
  return `data:application/pdf;base64,${btoa('Mock PDF Content - This would be actual PDF binary data')}`;
};

const generateMockJSONContent = () => {
  return JSON.stringify({
    loan_id: "1048000010",
    borrower_name: "ABC Company Inc.",
    loan_amount: 2500000,
    interest_rate: 5.25,
    term_months: 360,
    extracted_fields: {
      dscr: "1.35",
      min_rate: "5.25%",
      max_rate: "6.75%",
      property_value: 3500000,
      ltv: "71.4%"
    },
    processing_metadata: {
      extracted_at: "2024-01-15T10:30:00Z",
      confidence_score: 0.92,
      pages_processed: 5
    }
  }, null, 2);
};

const generateMockCSVContent = () => {
  return [
    ['Field Name', 'Value', 'Source', 'Confidence'],
    ['Loan ID', '1048000010', 'Master File', '100%'],
    ['Borrower Name', 'ABC Company Inc.', 'Master File', '100%'],
    ['Loan Amount', '$2,500,000', 'Master File', '100%'],
    ['Interest Rate', '5.25%', 'Proposal', '95%'],
    ['Term', '360 months', 'Master File', '100%'],
    ['DSCR', '1.35', 'Proposal', '90%'],
    ['Property Value', '$3,500,000', 'Note', '88%'],
    ['LTV', '71.4%', 'Calculated', '100%']
  ];
};

const generateMockTextContent = () => {
  return `PROMISSORY NOTE

This Promissory Note is made and entered into on January 15, 2024, between ABC Company Inc., a Delaware corporation (the "Borrower"), and XYZ Bank, a national banking association (the "Lender").

LOAN TERMS:
- Principal Amount: $2,500,000.00
- Interest Rate: 5.25% per annum
- Term: 360 months (30 years)
- Monthly Payment: $13,804.17

COLLATERAL:
The loan is secured by a first mortgage lien on the property located at:
123 Business Park Drive, Suite 100
Anytown, ST 12345

BORROWER COVENANTS:
1. Maintain property insurance coverage
2. Pay all property taxes when due
3. Maintain DSCR of not less than 1.25x
4. Provide annual financial statements

DEFAULT PROVISIONS:
In the event of default, the Lender may:
- Accelerate the entire unpaid balance
- Foreclose on the collateral
- Pursue all available remedies at law or equity

This Note is governed by the laws of the State of Delaware.

BORROWER: ABC Company Inc.
By: John Smith, President
Date: January 15, 2024

LENDER: XYZ Bank
By: Jane Doe, Vice President
Date: January 15, 2024`;
};

function FileViewer({ 
  isOpen = false, 
  onClose = () => {}, 
  document = null, 
  mockContent = null,
  showMockControls = false,
  documents = [], // Array of all documents for this loan
  onDocumentSelect = null // Callback when a document is selected
}) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mock content state for Storybook
  const [mockFileType, setMockFileType] = useState('pdf');
  const [mockFileName, setMockFileName] = useState('sample_document.pdf');

  // Sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true); // Default to collapsed
  
  // Determine which group should be expanded based on current document
  const getExpandedGroupsForDocument = (currentDoc) => {
    if (!currentDoc) return { master: false, proposal: false, note: false, security: false, other: true };
    
    // Determine the group type for the current document
    let activeGroupType = 'other';
    
    if (currentDoc.type === 'source') {
      const fileName = currentDoc.fileName || currentDoc.name || currentDoc.s3Key || '';
      const lowerFileName = fileName.toLowerCase();
      
      if (lowerFileName.includes('master')) {
        activeGroupType = 'master';
      } else if (lowerFileName.includes('proposal')) {
        activeGroupType = 'proposal';
      } else if (lowerFileName.includes('note')) {
        activeGroupType = 'note';
      } else if (lowerFileName.includes('security')) {
        activeGroupType = 'security';
      }
    } else if (currentDoc.type === 'keyvalues' || currentDoc.type === 'textract') {
      const fileName = currentDoc.fileName || currentDoc.name || currentDoc.s3Key || '';
      const lowerFileName = fileName.toLowerCase();
      
      if (lowerFileName.includes('master')) {
        activeGroupType = 'master';
      } else if (lowerFileName.includes('proposal')) {
        activeGroupType = 'proposal';
      } else if (lowerFileName.includes('note')) {
        activeGroupType = 'note';
      } else if (lowerFileName.includes('security')) {
        activeGroupType = 'security';
      }
    }
    
    return {
      master: activeGroupType === 'master',
      proposal: activeGroupType === 'proposal',
      note: activeGroupType === 'note',
      security: activeGroupType === 'security',
      other: activeGroupType === 'other'
    };
  };
  
  const [expandedGroups, setExpandedGroups] = useState(getExpandedGroupsForDocument(document));

  // Update expanded groups when document changes, but preserve existing expanded state
  useEffect(() => {
    const newExpandedGroups = getExpandedGroupsForDocument(document);
    setExpandedGroups(prev => ({
      ...prev,
      ...newExpandedGroups
    }));
  }, [document]);

  // Group documents by type
  const groupedDocuments = documents.reduce((groups, doc) => {
    // Map document types to group names
    let groupType = 'other';
    
    if (doc.type === 'source') {
      // For source documents, try to determine type from filename or s3Key
      const fileName = doc.fileName || doc.name || doc.s3Key || '';
      const lowerFileName = fileName.toLowerCase();
      
      if (lowerFileName.includes('master')) {
        groupType = 'master';
      } else if (lowerFileName.includes('proposal')) {
        groupType = 'proposal';
      } else if (lowerFileName.includes('note')) {
        groupType = 'note';
      } else if (lowerFileName.includes('security')) {
        groupType = 'security';
      }
    } else if (doc.type === 'keyvalues') {
      // KeyValues documents should be grouped with their source type
      const fileName = doc.fileName || doc.name || doc.s3Key || '';
      const lowerFileName = fileName.toLowerCase();
      
      if (lowerFileName.includes('master')) {
        groupType = 'master';
      } else if (lowerFileName.includes('proposal')) {
        groupType = 'proposal';
      } else if (lowerFileName.includes('note')) {
        groupType = 'note';
      } else if (lowerFileName.includes('security')) {
        groupType = 'security';
      }
    } else if (doc.type === 'textract') {
      // Textract documents should be grouped with their source type
      const fileName = doc.fileName || doc.name || doc.s3Key || '';
      const lowerFileName = fileName.toLowerCase();
      
      if (lowerFileName.includes('master')) {
        groupType = 'master';
      } else if (lowerFileName.includes('proposal')) {
        groupType = 'proposal';
      } else if (lowerFileName.includes('note')) {
        groupType = 'note';
      } else if (lowerFileName.includes('security')) {
        groupType = 'security';
      }
    } else if (['master', 'proposal', 'note', 'security'].includes(doc.type)) {
      groupType = doc.type;
    }
    
    if (!groups[groupType]) {
      groups[groupType] = [];
    }
    groups[groupType].push(doc);
    return groups;
  }, {});

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  // Toggle group expansion
  const toggleGroup = (groupType) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupType]: !prev[groupType]
    }));
  };

  // Handle document selection
  const handleDocumentSelect = (selectedDoc) => {
    if (onDocumentSelect) {
      onDocumentSelect(selectedDoc);
    }
  };

  // File type colors matching the circle design
  const fileTypeColors = {
    pdf: '#e74c3c',
    csv: '#27ae60', 
    json: '#f39c12',
    txt: '#3498db',
    xml: '#9b59b6',
    xlsx: '#27ae60',
    xls: '#27ae60',
    tif: '#8e44ad',
    tiff: '#8e44ad',
    jpg: '#8e44ad',
    jpeg: '#8e44ad'
  };

  // Get file type circle component
  const getFileTypeCircle = (doc) => {
    let extension = 'txt';
    
    // First, check if document has a type field that indicates the content type
    if (doc.type === 'keyvalues' || doc.type === 'textract') {
      extension = 'json';
    } else if (doc.type === 'source') {
      // For source files, determine from filename or s3Key
      const fileName = doc.fileName || doc.name || doc.s3Key || '';
      extension = fileName.split('.').pop()?.toLowerCase() || 'txt';
    } else {
      // Fallback to filename extension
      const fileName = doc.fileName || doc.name || doc.s3Key || '';
      extension = fileName.split('.').pop()?.toLowerCase() || 'txt';
    }
    
    const color = fileTypeColors[extension] || fileTypeColors.txt;
    const displayText = extension === 'xlsx' || extension === 'xls' ? 'csv' : 
                       extension === 'jpeg' ? 'jpg' : extension;
    
    return (
      <FileTypeCircle color={color}>
        {displayText}
      </FileTypeCircle>
    );
  };

  // Format uploaded date
  const formatUploadDate = (doc) => {
    const uploadDate = doc.uploadedAt || doc.createdAt || doc.dateUploaded;
    if (!uploadDate) return null;
    
    try {
      const date = new Date(uploadDate);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return 'Today';
      if (diffDays === 2) return 'Yesterday';
      if (diffDays <= 7) return `${diffDays - 1} days ago`;
      if (diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadContent();
    }
  }, [isOpen, document, mockContent, mockFileType, mockFileName]);

  const loadContent = async () => {
    setLoading(true);
    setError(null);

    try {
      // Simulate loading delay
      await new Promise(resolve => setTimeout(resolve, 500));

      if (mockContent) {
        setContent(mockContent);
      } else if (document) {
        // Handle real document loading (same as original DocumentPreview)
        if (document && document.s3Key) {
          await loadS3Content(document.s3Key);
        } else if (document) {
          await loadFileContent(document);
        }
      } else {
        // Generate mock content based on selected type
        const mockDoc = {
          name: mockFileName,
          fileName: mockFileName,
          type: mockFileType === 'keyvalues' ? 'keyvalues' : 'source'
        };
        const mockContent = generateMockContent(mockDoc);
        setContent(mockContent);
      }
    } catch (err) {
      console.error('Error loading content:', err);
      setError(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const generateMockContent = (doc) => {
    const extension = getFileExtension(doc);
    
    switch (extension) {
      case 'pdf':
        return { type: 'pdf', url: generateMockPDFContent() };
      case 'json':
        return { type: 'json', data: generateMockJSONContent() };
      case 'csv':
        return { type: 'csv', data: generateMockCSVContent() };
      case 'txt':
        return { type: 'text', text: generateMockTextContent() };
      default:
        return { type: 'unsupported', fileName: doc.fileName };
    }
  };

  const getFileExtension = (doc) => {
    if (!doc) return 'txt';
    
    try {
      if (doc.type) {
        switch (doc.type) {
          case 'keyvalues':
          case 'textract':
            return 'json';
          case 'source':
            const fileName = doc.fileName || doc.name || '';
            return fileName.split('.').pop().toLowerCase();
          default:
            return 'txt';
        }
      }
      
      const fileName = doc.fileName || doc.name || '';
      return fileName.split('.').pop().toLowerCase() || 'txt';
    } catch (error) {
      console.warn('Error getting file extension:', error);
      return 'txt';
    }
  };

  const loadS3Content = async (s3Key) => {
    try {
      const response = await databaseAPI.getDocumentContent(s3Key);
      
      if (response.data.success) {
        const { content, type, content_type } = response.data.data;
        
        switch (type) {
          case 'pdf':
            // For PDFs, create a blob URL from base64
            const binaryString = atob(content);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            setContent({ type: 'pdf', url: blobUrl });
            break;
            
          case 'json':
            // For JSON, display as formatted JSON
            setContent({ type: 'json', data: content });
            break;
            
          case 'text':
            // For text files, display as text
            setContent({ type: 'text', text: content });
            break;
            
          case 'binary':
            // Check file type by extension
            if (s3Key.toLowerCase().includes('.tif') || s3Key.toLowerCase().includes('.tiff')) {
              setContent({ type: 'tif', fileName: s3Key.split('/').pop(), s3Key: s3Key });
            } else if (s3Key.toLowerCase().includes('.jpg') || s3Key.toLowerCase().includes('.jpeg')) {
              // For JPEG files, create a blob URL from base64
              const binaryString = atob(content);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: 'image/jpeg' });
              const blobUrl = URL.createObjectURL(blob);
              setContent({ type: 'image', url: blobUrl, fileName: s3Key.split('/').pop() });
            } else {
              // For other binary files, show as base64 (fallback)
              setContent({ type: 'text', text: `Binary file (base64):\n${content.substring(0, 1000)}...` });
            }
            break;
            
          default:
            setContent({ type: 'text', text: content });
        }
      } else {
        setError(response.data.error || 'Failed to load document from S3');
      }
    } catch (err) {
      console.error('Error loading S3 content:', err);
      setError('Failed to load document from S3');
    }
  };

  const loadFileContent = async (file) => {
    // Mock file content loading for Storybook
    const mockContent = generateMockContent(file);
    setContent(mockContent);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner size="large" />
          <p>Loading document...</p>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorContainer>
          <div style={{ fontSize: '48px' }}>📄</div>
          <h3>Error Loading Document</h3>
          <p>{error}</p>
        </ErrorContainer>
      );
    }

    if (!content) {
      return (
        <UnsupportedPreview>
          <FileIcon>📄</FileIcon>
          <h3>No Content</h3>
          <p>No document content available for preview.</p>
        </UnsupportedPreview>
      );
    }

    switch (content.type) {
      case 'pdf':
        return (
          <PDFPreview
            src={content.url}
            title="PDF Preview"
          />
        );

      case 'json':
        return (
          <JSONPreview>
            {typeof content.data === 'string' ? content.data : JSON.stringify(content.data, null, 2)}
          </JSONPreview>
        );

      case 'csv':
        return (
          <TableContainer>
            <CSVTable>
              <thead>
                <tr>
                  {content.data[0]?.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {content.data.slice(1).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </CSVTable>
          </TableContainer>
        );

      case 'text':
        return (
          <TextPreview>
            {content.text}
          </TextPreview>
        );

      case 'excel':
        return (
          <ExcelPreview>
            <div style={{ fontSize: '48px' }}>📊</div>
            <h3>Excel File</h3>
            <p>Excel preview functionality would be implemented here.</p>
            <p>File: {content.fileName}</p>
          </ExcelPreview>
        );

      case 'tif':
        return (
          <UnsupportedPreview>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🖼️</div>
              <h3>Cannot preview this file type</h3>
              <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                TIF files cannot be previewed in the browser.
              </p>
            </div>
          </UnsupportedPreview>
        );

      case 'image':
        return (
          <ImagePreview>
            <img 
              src={content.url} 
              alt={content.fileName || 'Image preview'}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain' 
              }}
            />
          </ImagePreview>
        );

      default:
        return (
          <UnsupportedPreview>
            <FileIcon>📄</FileIcon>
            <h3>Unsupported File Type</h3>
            <p>Preview not available for {content.fileName || 'this file type'}.</p>
          </UnsupportedPreview>
        );
    }
  };

  // Render document sidebar
  const renderSidebar = () => {
    if (documents.length === 0) return null;

    const groupConfig = {
      master: { icon: FileText, label: 'Master' },
      proposal: { icon: FileCheck, label: 'Proposal' },
      note: { icon: FileSignature, label: 'Note' },
      security: { icon: Shield, label: 'Security' },
      other: { icon: FileText, label: 'Other' }
    };

    return (
      <>
        <Sidebar isCollapsed={isSidebarCollapsed}>
          <SidebarHeader>
            📁 Documents ({documents.length})
          </SidebarHeader>
          
              {Object.entries(groupedDocuments).map(([type, docs]) => {
          const config = groupConfig[type] || groupConfig.other;
          const isExpanded = expandedGroups[type];
          const IconComponent = config.icon;
          
          return (
            <DocumentGroup key={type}>
              <GroupHeader 
                isExpanded={isExpanded}
                onClick={() => toggleGroup(type)}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <GroupIcon>
                    <IconComponent size={16} color="#6b7280" />
                  </GroupIcon>
                  {config.label} ({docs.length})
                </div>
                <ExpandIcon isExpanded={isExpanded}>
                  <ChevronDown size={16} />
                </ExpandIcon>
              </GroupHeader>
              
              <DocumentList isExpanded={isExpanded}>
                {docs.map((doc, index) => {
                  // Use s3Key for comparison since id is undefined
                  const isActive = document && doc.s3Key === document.s3Key;
                  const fileName = doc.fileName || doc.name || `Document ${index + 1}`;
                  const uploadDate = formatUploadDate(doc);
                  
                  return (
                    <DocumentItem
                      key={doc.id || index}
                      isActive={isActive}
                      onClick={() => handleDocumentSelect(doc)}
                    >
                      {getFileTypeCircle(doc)}
                      <DocumentInfo>
                        <DocumentName title={fileName}>
                          {fileName}
                        </DocumentName>
                        {uploadDate && (
                          <DocumentDate>
                            {uploadDate}
                          </DocumentDate>
                        )}
             {/* JSON buttons */}
             {(doc.s3_textract_key || doc.s3_keyvalues_key) && (
               <DocumentButtons>
                 {doc.s3_textract_key && (
                   <DocumentButton
                     onClick={(e) => {
                       e.stopPropagation();
                       handleDocumentSelect({ ...doc, s3Key: doc.s3_textract_key, type: 'textract' });
                     }}
                     title="View Textract JSON"
                   >
                     Textract
                   </DocumentButton>
                 )}
                 {doc.s3_textract_key && doc.s3_keyvalues_key && (
                   <span style={{ color: '#6b7280', fontSize: '0.75rem' }}> | </span>
                 )}
                 {doc.s3_keyvalues_key && (
                   <DocumentButton
                     onClick={(e) => {
                       e.stopPropagation();
                       handleDocumentSelect({ ...doc, s3Key: doc.s3_keyvalues_key, type: 'keyvalues' });
                     }}
                     title="View KeyValues JSON"
                   >
                     KeyValues
                   </DocumentButton>
                 )}
               </DocumentButtons>
             )}
                      </DocumentInfo>
                    </DocumentItem>
                  );
                })}
              </DocumentList>
            </DocumentGroup>
          );
        })}
        </Sidebar>
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <PreviewContainer>
      <PreviewModal hasSidebar={documents.length > 0}>
        {renderSidebar()}
        <MainContent>
          <SidebarToggle 
            isCollapsed={isSidebarCollapsed}
            onClick={toggleSidebar}
            title={isSidebarCollapsed ? 'Show document list' : 'Hide document list'}
          >
            {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </SidebarToggle>
          <PreviewHeader hasSidebar={documents.length > 0}>
            <PreviewTitle title={document?.s3Key || mockFileName}>
              <span style={{ fontSize: '20px' }}>📄</span>
              {document?.name || document?.fileName || mockFileName}
            </PreviewTitle>
            
            
            <HeaderActions>
              {showMockControls && (
                <>
                  <select 
                    value={mockFileType} 
                    onChange={(e) => setMockFileType(e.target.value)}
                    style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="pdf">PDF</option>
                    <option value="json">JSON</option>
                    <option value="csv">CSV</option>
                    <option value="txt">Text</option>
                  </select>
                  <input 
                    type="text" 
                    value={mockFileName} 
                    onChange={(e) => setMockFileName(e.target.value)}
                    placeholder="Filename"
                    style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
                  />
                </>
              )}
              <CloseButton onClick={onClose} title="Close document viewer">
                <X size={18} />
              </CloseButton>
            </HeaderActions>
          </PreviewHeader>
          
          <PreviewContent>
            {renderContent()}
          </PreviewContent>
        </MainContent>
      </PreviewModal>
    </PreviewContainer>
  );
}

export default FileViewer;
